<template>
  <div
    :class="[
      $style.layer,
      {
        [$style.is24]: isIs24,
        [$style.hg]: !isIs24,
        [$style.expanded]: !!isDataLayerExpanded,
        [$style.collapsed]: !isDataLayerExpanded,
      },
    ]"
    data-testid="data-view-layer"
  >
    <div
      ref="dataLayer"
      :class="$style.contentWrapper"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <div
        v-if="!isIs24"
        ref="pullingBar"
        :class="$style.pullingBarWrapper"
        @click="handlePullingBarClick"
      >
        <div :class="$style.pullingBar" data-testid="pulling-bar" />
      </div>
      <SideBar data-testid="side-bar" @onFeaturedAgenciesDemo="handlePullingBarClick" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, inject, ref } from 'vue';

import SideBar from '@/components/side-bar/SideBar.vue';
import { isPlatformIs24 } from '@/helpers/platform';
import useMapStore from '@/stores/useMapStore';
import { type MediaContext } from '@/views/composables/useMedia';

const touchEnabledSectionIds = ['#median-price', '#price-trends'];
// TODO: cuz the element of modal is not inserted on the body, causing the touch event will impact the modal scrolling. If wants to get rid of this logic, needs the HgModal supports to insert to body.
const touchDisabledSectionIds = ['#median-price-modal', '#price-trends-modal'];

const isIs24 = isPlatformIs24();
const mapStore = useMapStore();
const { isDataLayerExpanded } = storeToRefs(mapStore);
const { isSmallScreen } = inject<MediaContext>('mediaScreen') as MediaContext;

const dataLayer = ref();
const startY = ref<number>(0);
const endY = ref<number>(0);
const pullingBar = ref();

const clearY = (): void => {
  startY.value = 0;
  endY.value = 0;
};

const isPullingUp = computed<boolean>(() => {
  const deltaY = endY.value - startY.value;
  return deltaY < 0 && !isDataLayerExpanded.value;
});

const isPullingDown = computed<boolean>(() => {
  const deltaY = endY.value - startY.value;
  return deltaY > 0 && isDataLayerExpanded.value && dataLayer.value?.scrollTop === 0;
});

const isTouchingEnabled = (event: TouchEvent): boolean => {
  const isOnHgMobile = !isIs24 && !!isSmallScreen.value;
  const isOnTargetSections = touchEnabledSectionIds.some((id) =>
    (event.target as any)?.closest(id),
  );
  const isOnModalSections = touchDisabledSectionIds.some((id) =>
    (event.target as any)?.closest(id),
  );
  return isOnHgMobile && isOnTargetSections && !isOnModalSections && event.cancelable;
};

const handleTouchStart = (event: TouchEvent): void => {
  if (isTouchingEnabled(event)) {
    startY.value = event.touches[0].clientY;
  }
};

const handleTouchMove = (event: TouchEvent): void => {
  if (isTouchingEnabled(event)) {
    endY.value = event.touches[0].clientY;
    if (isPullingUp.value || isPullingDown.value) {
      event.preventDefault();
    }
  }
};

const handleTouchEnd = (): void => {
  if (!startY.value || !endY.value) {
    clearY();
    return;
  }
  if (isPullingUp.value || isPullingDown.value) {
    mapStore.toggleDataLayer();
  }
  clearY();
};

const handlePullingBarClick = () => {
  mapStore.toggleDataLayer();
};
</script>

<style lang="scss" module>
@use '@/styles/variables' as *;

$default-layer-height: 138px;
$search-bar-height: 190px;

.layer {
  position: fixed;
  bottom: 0;
  z-index: $hg-mobile-data-layer-zindex;
  box-sizing: content-box;
  width: 100%;
  background-color: white;
  border-top-left-radius: $border-radius-xl;
  border-top-right-radius: $border-radius-xl;
  box-shadow: 0 4px 16px 0 rgb(0 0 0 / 25%);
  transition: height 0.5s ease;

  .contentWrapper {
    height: 100%;
  }

  &.hg {
    &.expanded {
      height: calc(100% - $search-bar-height - $space-400);

      .contentWrapper {
        overflow: auto;
      }
    }

    &.collapsed {
      height: $default-layer-height;
    }

    .pullingBarWrapper {
      padding: $space-50;
      margin-bottom: $space-50;
      cursor: pointer;
    }

    .pullingBar {
      width: 30px;
      height: 4px;
      margin: 0 auto;
      background-color: $color-grey-50;
      border-radius: $border-radius-s;
    }

    .contentWrapper {
      padding: $space-50 $space-100;
    }
  }

  &.is24 {
    position: static;
    height: auto;
    border-radius: unset;
    box-shadow: none;

    .contentWrapper {
      padding: $space-100;
    }
  }

  @include grid-media-query(sm) {
    position: static;
    border-radius: 0;
    box-shadow: none;

    &.hg {
      &.expanded,
      &.collapsed {
        height: auto;
      }

      .contentWrapper {
        padding: $space-100;
      }
    }

    .pullingBarWrapper {
      display: none;
    }
  }
}
</style>
